import { FC } from 'react';

const HomePage: FC = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4">AI-Enhanced Marketing for Financial Service Providers</h1>
              <p className="text-xl mb-8">
                DLDV Enterprises leverages cutting-edge AI and automation to optimize marketing and business development for investment firms and financial service providers.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <a href="/services" className="btn-primary text-center">Explore Services</a>
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-secondary text-center">Contact Us</a>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img src="/images/YON_6653.JPG" alt="Dirk de Vlaam - Founder & CEO of DLDV Enterprises" className="w-full max-w-md rounded-lg shadow-lg animate-slide-up" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Services</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Placement Agent Services</h3>
              <p className="mb-4">
                Connect with qualified investors through our AI-enhanced placement agent services, optimized for investment firms seeking efficient capital raising.
              </p>
              <a href="/services" className="text-accent-color hover:underline">Learn more →</a>
            </div>
            
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Document Creation</h3>
              <p className="mb-4">
                Produce compelling marketing materials, pitch decks, and investment memoranda with our AI-powered document creation services.
              </p>
              <a href="/services" className="text-accent-color hover:underline">Learn more →</a>
            </div>
            
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">AI & Automation</h3>
              <p className="mb-4">
                Transform your marketing operations with custom AI solutions designed specifically for financial service providers.
              </p>
              <a href="/ai-automation" className="text-accent-color hover:underline">Learn more →</a>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <a href="/services" className="btn-primary">View All Services</a>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Why Choose DLDV Enterprises</h2>
              <p className="mb-6">
                We combine deep financial industry expertise with cutting-edge AI technology to deliver exceptional results for investment firms and financial service providers.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="text-accent-color mr-4 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Industry Expertise</h3>
                    <p>Specialized knowledge of financial services marketing requirements and regulations</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="text-accent-color mr-4 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">AI-Powered Efficiency</h3>
                    <p>Automation that reduces costs while improving quality and consistency</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="text-accent-color mr-4 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Tailored Solutions</h3>
                    <p>Customized approaches that address your specific business challenges</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="text-accent-color mr-4 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Measurable Results</h3>
                    <p>Data-driven strategies with clear ROI and performance metrics</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <a href="/about" className="btn-secondary">About Us</a>
              </div>
            </div>
            
            <div className="flex justify-center">
              <div className="card p-8 w-full max-w-md">
                <h3 className="text-2xl font-bold mb-6 text-center">Transforming Financial Marketing</h3>
                <p className="mb-6 text-center">
                  Our AI-enhanced solutions have helped investment firms achieve:
                </p>
                
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-accent-color mb-2">40%</div>
                    <p>Reduction in document creation time</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-4xl font-bold text-accent-color mb-2">3x</div>
                    <p>Increase in qualified investor leads</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-4xl font-bold text-accent-color mb-2">60%</div>
                    <p>More efficient marketing operations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="section bg-primary-color text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Financial Marketing?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Discover how DLDV Enterprises can help your investment firm leverage AI and automation to achieve exceptional results.
          </p>
          <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary bg-white text-primary-color hover:bg-gray-100">Contact Us Today</a>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
